import XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import i18n from '@/i18n';
import utils from '@/util';
import moment from 'moment';
import user from '@/store/modules/user';

/*
    Doc:
    - https://github.com/simonbengtsson/jsPDF-AutoTable

*/

const colorSuccess = [2, 188, 119];
const colorDanger = [217, 84, 79];

function fitToColumn(arrayOfArray) {
  if (!Array.isArray(arrayOfArray)) return [];

  return arrayOfArray[0].map((_, i) => ({
    wch: Math.max(...arrayOfArray.map((a2) => (a2[i] ? a2[i].toString().length : 0)))
  }));
}

function jsonToXlsx({ name = '', headers = [], data = [], colsWidth = [] } = {}) {
  const dt = data || [];
  const dataRows = dt.map((item) => {
    return Object.values(item);
  });

  dataRows.unshift(headers);

  const ws = XLSX.utils.aoa_to_sheet(dataRows);

  if (colsWidth && colsWidth.length > 0) {
    ws['!cols'] = colsWidth;
  } else {
    ws['!cols'] = fitToColumn(dataRows);
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'SheetJS');
  XLSX.writeFile(wb, `${name}.xlsx`);
}

function jsonToCsv({ name = '', headers = [], data = [] } = {}) {
  // Função para converter dados JSON em CSV
  const convertToCSV = (dataset) => {
    const header = headers; // Cabeçalho
    const rows = dataset.map((obj) => Object.values(obj).join(',')); // Linhas
    return [header, ...rows].join('\n');
  };

  // Função para gerar o arquivo CSV e iniciar o download
  const downloadCSV = (filename, csvData) => {
    const blob = new Blob(['\uFEFF' + csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Convertendo e fazendo o download
  const csvData = convertToCSV(data);
  downloadCSV(`${name}.csv`, csvData);
}

function jsonToXml({ name = '', headers = [], data = [], rowTag = 'item' } = {}) {
  /**
   * Escapa caracteres especiais de uma string para uso seguro em XML.
   * Substitui os seguintes caracteres por suas respectivas entidades:
   * - &  -> &amp;
   * - <  -> &lt;
   * - >  -> &gt;
   * - "  -> &quot;
   * - '  -> &apos;
   *
   * Isso é útil para evitar problemas ao inserir valores em documentos XML,
   * garantindo que caracteres especiais sejam tratados como texto literal.
   *
   * @param {string} value - A string que será escapada.
   * @returns {string} - A string com os caracteres escapados.
   */
  const escapeXmlValue = (value) => {
    return `${value}`
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  const buildHeaderXML = () => {
    let headerXML = '';
    headerXML += `<momentoEmissao>${utils.formatDateTime(new Date())}</momentoEmissao>`;
    headerXML += `<emitidoPor>${user?.state?.info.email}</emitidoPor>`;
    headerXML += `<nomeRelatorio>${name}</nomeRelatorio>`;
    headerXML += `<empresa>${user?.state?.info.empresa__nome}</empresa>`;
    return headerXML;
  };

  const convertToXML = (dataset) => {
    const validRowTag = utils.formatXmlTag(rowTag);
    const validName = utils.formatXmlTag(name);
    const namespace = escapeXmlValue(window.location.href);
    let xml = `<?xml version="1.0" encoding="UTF-8"?>`;
    xml += `<atena:backOfficeAtena xmlns:atena="${namespace}">`;

    xml += buildHeaderXML();
    xml += `<${validName}>`;

    dataset.forEach((row) => {
      xml += `<${validRowTag}>`;
      headers.forEach(({ key, label }) => {
        const normalizedLabel = utils.formatXmlTag(label);
        const value = row[key] || '';
        xml += `<${normalizedLabel}>${escapeXmlValue(value)}</${normalizedLabel}>`;
      });
      xml += `</${validRowTag}>`;
    });

    xml += `</${validName}>`;
    xml += `</atena:backOfficeAtena>`;
    return xml;
  };

  const downloadXML = (filename, xmlData) => {
    const blob = new Blob([xmlData], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const xmlData = convertToXML(data);
  downloadXML(`${name}_${utils.formatDate(new Date())}.xml`, xmlData);
}

function jsonToXlsxMultiples({ name = '', headers = [], data = [], colsWidth = [], titles = [] } = {}) {
  let dataRows = [];
  let ws = [];
  const wb = XLSX.utils.book_new();
  if (titles.length > 0) {
    titles.forEach((title, index) => {
      dataRows[index] = data[index].map((item) => {
        return Object.values(item);
      });
      dataRows[index].unshift(headers);
      ws[index] = XLSX.utils.aoa_to_sheet(dataRows[index]);
      if (colsWidth && colsWidth.length > 0) {
        ws['!cols'] = colsWidth;
      } else {
        ws['!cols'] = fitToColumn(dataRows[index]);
      }
      XLSX.utils.book_append_sheet(wb, ws[index], title);
    });
  } else {
    const dt = data || [];
    dataRows = dt.map((item) => {
      return Object.values(item);
    });

    dataRows.unshift(headers);
    ws = XLSX.utils.aoa_to_sheet(dataRows);
    if (colsWidth && colsWidth.length > 0) {
      ws['!cols'] = colsWidth;
    } else {
      ws['!cols'] = fitToColumn(dataRows);
    }
    XLSX.utils.book_append_sheet(wb, ws[index], 'planilha');
  }

  XLSX.writeFile(wb, `${name}.xlsx`);
}

export const docPDF = (config) => {
  return new jsPDF(config);
};

//colors
export const COLORS = {
  primary: [38, 179, 255],
  second: [64, 188, 255],
  success: [2, 188, 119],
  danger: [217, 84, 79],
  bg: [38, 179, 255],
  black: 0,
  white: 255
};

export const addTitle = (doc, title) => {
  doc.setFontSize(15);
  doc.setFont('Helvetica', 'bold');
  doc.setTextColor(40);

  doc.text(i18n.t(title) || i18n.t('Relatório'), 15, 22);
  doc.setFont('Helvetica', 'normal');
};

export const addHeaderElement = (doc, header, positionX, positionY) => {
  doc.setFontSize(13);
  doc.setFont('Helvetica', 'bold');
  doc.setTextColor(40);

  doc.text(i18n.t(title), positionX, positionY);
  doc.setFont('Helvetica', 'normal');
};

export const addFilter = (doc, filtros) => {
  doc.setFontSize(12);
  doc.text(i18n.t('Filtros') + ': ', 15, 28);
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(filtros, 30, 28, { maxWidth: 250 });
};

export const addUserInfo = (doc, userInfo) => {
  const username = userInfo?.nome || i18n.t('Anônimo');
  const date = utils.formatDateTime(moment());
  const x_pos = parseFloat(doc.internal.pageSize.getWidth()) - 47;

  doc.setFont('Helvetica', 'bold');
  doc.setTextColor(40);
  doc.setFontSize(12);
  doc.text(userInfo.empresa__nome || '', 15, 16);

  doc.setFont('Helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(username, x_pos, 16);
  doc.text(date, x_pos, 20);
  doc.setTextColor(100, 100, 100);
};

export const addFilterPeriodo = (doc, showFilters, periodo) => {
  let cordTextPeriodoX = showFilters ? 15 : 15;
  let cordTextPeriodoY = showFilters ? 36 : 28;
  let cordResultPeriodoX = showFilters ? 32 : 32;
  let cordResultPeriodoY = showFilters ? 36 : 28;

  let textPeriodo,
    resultPeriodo = '';
  if (periodo.data_inicio && !periodo.data_fim) {
    resultPeriodo = periodo.data_inicio;
    textPeriodo = i18n.t('Data') + ': ';
    cordResultPeriodoX = 26;
  } else {
    resultPeriodo = `${periodo.data_inicio} - ${periodo.data_fim}`;
    textPeriodo = i18n.t('Período') + ': ';
  }

  //Período
  doc.setTextColor(0);
  doc.setFontSize(12);
  doc.text(textPeriodo, cordTextPeriodoX, cordTextPeriodoY);
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(resultPeriodo, cordResultPeriodoX, cordResultPeriodoY);
};

export const addFooters = (doc, legenda) => {
  const pageCount = doc.internal.getNumberOfPages();
  doc.setFontSize(10);
  doc.setTextColor(200, 200, 200);
  const legendaHeader = 'Legenda:';
  let legendaText = null;
  if (legenda && legenda.length > 0) {
    legendaText = legenda.map((item) => `${item.header}: ${item.result}`).join(' | ');
  }

  for (var i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setTextColor(33, 33, 33);
    doc.text(
      String(i) + ' / ' + String(pageCount),
      doc.internal.pageSize.getWidth() - 20,
      doc.internal.pageSize.getHeight() - 10,
      {
        align: 'center'
      }
    );
    if (legenda && legenda.length > 0) {
      doc.setTextColor(100, 100, 100);
      doc.text(legendaHeader, 20, doc.internal.pageSize.getHeight() - 15);
      doc.text(legendaText, 20, doc.internal.pageSize.getHeight() - 10, { align: 'left' });
    }
  }
};

const formatedHeader = (arrHeader, data) => {
  const keys = arrHeader.map((obj) => obj.dataKey);
  const emptyColumn = utils.emptyColumn(data, keys);
  return arrHeader.filter((obj) => !emptyColumn.includes(obj.dataKey));
};

const setRulesCellColor = (rule, value) => {
  const rules = {
    default: value > 0 ? colorSuccess : value < 0 ? colorDanger : ''
  };
  return rules[rule];
};

export const addTableDefault = async (doc, table) => {
  const margins = { top: 50, right: 10, left: 10, bottom: 20, width: 100 };

  const configTableDefault = {
    theme: 'plain',
    tableWidth: 'auto',
    columnWidth: 'auto',
    showHead: 'everyPage',
    styles: { overflow: 'linebreak', fontSize: 8, cellPadding: 2 }, //overflow: 'ellipsize'
    columnStyles: { text: { cellWidth: 'auto' } },
    headStyles: { fontStyle: 'bold', fillColor: COLORS.bg, overflow: 'linebreak' }
  };
  const configTable = table.config ? utils.merge(configTableDefault, table.config) : configTableDefault;

  const optionsAutoTable = {
    head: table.head,
    columns:
      table.verifyEmptyField || table.header
        ? table.verifyEmptyField
          ? formatedHeader(table.header, table.data)
          : table.header
        : table.columns,
    body: table.data,
    margin: margins,
    didParseCell: function (data) {
      if (table.changeColumns) {
        const { formatter, color } = table.changeColumns;
        if (color) {
          if (data.section === 'body' && color.keys.includes(data.column.dataKey)) {
            data.cell.styles.textColor = setRulesCellColor(color.rule, data.row.raw[data.column.dataKey]);
          }
        }
        if (formatter) {
          if (formatter.type === 'currency') {
            if (data.section === 'body' && formatter.keys.includes(data.column.dataKey)) {
              data.row.cells[data.column.dataKey].text[0] = utils.formatCurrency(
                data.row.raw[data.column.dataKey],
                { dynamicCurrency: true }
              );
            }
          }
        }
      }
    }
  };
  const formatedTable = utils.merge(optionsAutoTable, configTable);
  doc.autoTable(formatedTable);
};

export const addSumarioDefault = (doc, sumario) => {
  const colSumario = [
    {
      title: i18n.t(`${sumario.title}`),
      key: 'header'
    },
    {
      title: '',
      key: 'result'
    }
  ];
  const finalY = doc.lastAutoTable.finalY || 10;
  const configSumarioDefault = {
    startY: finalY + 20,
    pageBreak: 'avoid',
    tableWidth: 'auto',
    headStyles: { fillColor: COLORS.bg, color: COLORS.white, fontStyle: 'bold', valign: 'middle' },
    margin: { left: doc.internal.pageSize.getWidth() - 85 },
    styles: {
      overflow: 'linebreak',
      cellPadding: 2,
      minCellHeight: 10,
      fontSize: 10,
      textcolor: 0
    },
    columnStyles: {
      header: { fontStyle: 'bold', cellWidth: 40 },
      result: { cellWidth: 30 }
    },
    didParseCell: function (data) {
      if (data.section === 'body' && data.column.dataKey === 'result') {
        data.cell.styles.textColor = setRulesCellColor('default', data.row.raw.origin);
      }
    }
  };

  const configSumario = sumario.config ? utils.merge(configSumarioDefault, sumario.config) : configSumarioDefault;
  const dataFormatted = filterRows(sumario.data);
  doc.autoTable(colSumario, dataFormatted, configSumario);
};

export const addSumarioColHeader = (doc, sumario) => {
  const colSumario = [{ title: '' }];
  sumario.header.forEach((key) => {
    colSumario.push({
      title: key.nome == 'header' ? ' ' : key.nome
    });
  });
  let head = [[{ content: 'Sumário', colSpan: 4, styles: { halign: 'center', fillColor: COLORS.bg } }]];
  head.push(colSumario);
  const finalY = doc.lastAutoTable.finalY || 10;
  const configSumarioDefault = {
    head: head,
    body: sumario.data,
    startY: finalY + 80,
    pageBreak: 'avoid',
    tableWidth: 'auto',
    headStyles: { fillColor: second, color: white, fontStyle: 'bold', valign: 'middle' },
    margin: { left: doc.internal.pageSize.getWidth() - 140 },
    styles: {
      overflow: 'linebreak',
      cellPadding: 2,
      minCellHeight: 10,
      fontSize: 10,
      textcolor: 0,
      cellWidth: 30,
      halign: 'right'
    },
    columnStyles: {
      0: { fontStyle: 'bold', halign: 'left' }
    },
    didParseCell: function (data) {
      if (data.section === 'body' && data.column.dataKey === 'result') {
        data.cell.styles.textColor = setRulesCellColor('default', data.row.raw.origin);
      }
    }
  };
  const configSumario = sumario.config ? utils.merge(configSumarioDefault, sumario.config) : configSumarioDefault;
  doc.autoTable(configSumario);
};

export const addSuamrioCompability = (doc, sumario) => {
  const colSumario = [
    {
      header: i18n.t(`${sumario.title}`),
      dataKey: 'header'
    },
    {
      header: '',
      dataKey: 'result'
    }
  ];
  const finalY = doc.lastAutoTable.finalY || 10;
  const configSumarioDefault = {
    startY: finalY + 20,
    tableWidth: 'auto',
    headStyles: { fillColor: COLORS.bg, color: COLORS.white, fontStyle: 'bold', valign: 'middle' },
    margin: { left: doc.internal.pageSize.getWidth() - 85 },
    styles: { overflow: 'linebreak', cellPadding: 2, fontSize: 10 },
    columnStyles: { header: { fontStyle: 'bold' }, result: { halign: 'right' } }
  };
  const configSumario = sumario.config ? utils.merge(configSumarioDefault, sumario.config) : configSumarioDefault;
  doc.autoTable({ columns: colSumario, body: sumario.data, ...configSumario });
};

export const addTextToDoc = (doc, text, style = {}) => {
  const lastY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 50;
  const startY = lastY + 10;
  doc.text(text, 15, startY, style);
};

function jsonToPDF(jsPDFConfig, isReportDefault, header, table, sumario, legenda = []) {
  // Definir tipo do sumário como default caso venha null ou undefined
  if (!sumario) {
    sumario = { type: 'disabled' };
  } else if (!sumario.type) {
    sumario.type = 'default';
  }

  const jsPDFConfigDefault = { orientation: 'p', unit: 'pt', format: 'letter' };
  jsPDFConfig = jsPDFConfig ? utils.merge(jsPDFConfigDefault, jsPDFConfig) : jsPDFConfigDefault;
  const pdf = new jsPDF(Object.values(jsPDFConfig).toString());

  if (isReportDefault) {
    // Header
    addTitle(pdf, header.title);

    if (header.showFilters) {
      const filtros = header.filters ? header.filters : '';
      addFilter(pdf, filtros);
    }

    if (!header.hideUserDetails) {
      // Exibir nome do usuário e data de emissão do relatório
      // Para ocultar essa informação defina: hideUserDetails = true
      const userInfo = user?.state?.info;
      addUserInfo(pdf, userInfo);
    }

    if (header.showPeriodo) {
      addFilterPeriodo(pdf, header.showFilters, header.periodo);
    }
    if (table) {
      addTableDefault(pdf, table);
    }

    if (sumario.type == 'default') {
      addSumarioDefault(pdf, sumario);
    } else if (sumario.type == 'colHeader') {
      addSumarioColHeader(pdf, sumario);
    } else if (sumario.type == 'compatibility') {
      addSuamrioCompability(pdf, sumario);
    }
  }

  addFooters(pdf, legenda);
  return pdf;
}

function jsonToPDFWithChild(
  jsPDFConfig,
  header,
  json,
  nameChild,
  headerSource,
  headerChild,
  sumario,
  columnHandler = (v) => v
) {
  //colors
  const bg = [38, 179, 255];
  const bgSecond = [163, 209, 255];
  const black = 0;
  const white = 255;
  const primary = bg;
  const second = [64, 188, 255];
  const success = [2, 188, 119];
  const danger = [217, 84, 79];

  const headerSourceFormatted = filterRows(headerSource);
  const headersChildFormatted = filterRows(headerChild);

  const addFooters = (doc) => {
    const pageCount = doc.internal.getNumberOfPages();

    doc.setFontSize(10);
    doc.setTextColor(200, 200, 200);
    for (var i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setTextColor(33, 33, 33);
      doc.text(
        String(i) + ' / ' + String(pageCount),
        doc.internal.pageSize.getWidth() - 20,
        doc.internal.pageSize.getHeight() - 10,
        {
          align: 'center'
        }
      );
    }
  };

  const setRulesCellColor = (rule, value) => {
    const rules = {
      default: value > 0 ? colorSuccess : value < 0 ? colorDanger : ''
    };
    return rules[rule];
  };

  const jsPDFConfigDefault = { orientation: 'p', unit: 'pt', format: 'letter' };
  jsPDFConfig = jsPDFConfig ? utils.merge(jsPDFConfigDefault, jsPDFConfig) : jsPDFConfigDefault;

  const pdf = new jsPDF(Object.values(jsPDFConfig).toString());

  // Título
  pdf.setFontSize(15);
  pdf.setFont('Helvetica', 'bold');
  pdf.setTextColor(40);
  pdf.text(i18n.t(header.title) || i18n.t('Relatório'), 15, 16);
  pdf.setFont('Helvetica', 'normal');

  if (!header.hideUserDetails) {
    // Exibir nome do usuário e data de emissão do relatório
    // Para ocultar essa informação defina: hideUserDetails = true
    const username = user?.state?.info?.nome || i18n.t('Anônimo');
    const date = utils.formatDateTime(moment());
    const x_pos = parseFloat(pdf.internal.pageSize.getWidth()) - 47;

    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(100, 100, 100);

    pdf.text(username, x_pos, 16);
    pdf.text(date, x_pos, 20);
    pdf.setTextColor(100, 100, 100);
  }

  if (header.showCompany) {
    const company = header.company ? header.company : '';
    //Empresa
    pdf.setFontSize(12);
    pdf.text(i18n.t('Empresa') + ': ', 15, 22);
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text(company, 35, 22, { maxWidth: 150 });
  }

  if (header.showFilters) {
    const filtros = header.filters ? header.filters : '';
    //Filtros
    pdf.setFontSize(12);
    pdf.text(i18n.t('Filtros') + ': ', 15, 27);
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text(filtros, 30, 27, { maxWidth: 250 });
  }

  const configTableDefault = {
    theme: 'grid',
    tableWidth: 'auto',
    columnWidth: 'auto',
    showHead: 'everyPage',
    styles: { overflow: 'linebreak', fontSize: 8, cellPadding: 2 },
    columnStyles: { text: { cellWidth: 'auto' } },
    headStyles: { fontStyle: 'bold', fillColor: bg, textColor: white, overflow: 'linebreak' },
    margin: { right: 15, left: 15, bottom: 20, width: 100 },
    didParseCell: function (data) {
      if (data.cell.text[0].startsWith('Total')) {
        Object.keys(data.row.cells).forEach((item) => {
          data.row.cells[item].styles.fontStyle = 'bold';
        });
      }
      if (data.cell.text[0].startsWith('R$') || data.cell.text[0].startsWith('$') || !isNaN(data.cell.text[0])) {
        data.cell.styles.halign = 'right';
      }
      data = columnHandler(data);
    }
  };

  pdf.autoTable({
    ...configTableDefault,
    margin: { top: 40, right: 15, left: 15, bottom: 20, width: 100 },
    columns: headerSourceFormatted,
    body: [json[0]]
  });

  pdf.autoTable({
    ...configTableDefault,
    headStyles: { fontStyle: 'bold', fillColor: bgSecond, textColor: white, overflow: 'linebreak' },
    startY: pdf.lastAutoTable.finalY,
    columns: headersChildFormatted,
    body: json[0][nameChild]
  });

  json.forEach((item, index) => {
    if (index >= 1) {
      pdf.autoTable({
        ...configTableDefault,
        startY: pdf.lastAutoTable.finalY + 3,
        columns: headerSourceFormatted,
        body: [item]
      });

      pdf.autoTable({
        ...configTableDefault,
        headStyles: { fontStyle: 'bold', fillColor: bgSecond, textColor: white, overflow: 'linebreak' },
        startY: pdf.lastAutoTable.finalY,
        columns: headersChildFormatted,
        body: item[nameChild]
      });
    }
  });

  if (!sumario) {
    sumario = { type: 'disabled' };
  } else if (!sumario.type) {
    sumario.type = 'default';
  }

  if (sumario.type == 'default') {
    const colSumario = [
      {
        header: i18n.t(`${sumario.title}`),
        dataKey: 'header'
      },
      {
        header: '',
        dataKey: 'result'
      }
    ];
    const finalY = pdf.lastAutoTable.finalY || 10;
    const configSumarioDefault = {
      startY: finalY + 20,
      tableWidth: 'auto',
      headStyles: { fillColor: bg, color: white, fontStyle: 'bold', valign: 'middle' },
      margin: { left: pdf.internal.pageSize.getWidth() - 85 },
      styles: { overflow: 'linebreak', cellPadding: 2, fontSize: 10 },
      columnStyles: { header: { fontStyle: 'bold' }, result: { halign: 'right' } },
      didParseCell: function (data) {
        data = columnHandler(data, false);
      }
    };
    const configSumario = sumario.config
      ? utils.merge(configSumarioDefault, sumario.config)
      : configSumarioDefault;
    const dataFormatted = filterRows(sumario.data);
    pdf.autoTable({ columns: colSumario, body: dataFormatted, ...configSumario });
  }

  addFooters(pdf);
  return pdf;
}

function downloadPdf(doc, fileName, newWindow = true) {
  if (newWindow) {
    window.open(doc.output('bloburl', { filename: fileName }), '_blank');
    return;
  }
  doc.save(fileName || 'report' + '.pdf');
}

export const filterRows = (data) => {
  return data.filter((el) => {
    return !('show' in el) ? true : el.show;
  });
};

export default {
  jsonToXlsx,
  jsonToPDF,
  jsonToPDFWithChild,
  downloadPdf,
  jsonToXlsxMultiples,
  jsonToCsv,
  jsonToXml
};
